import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat, fn, get, hash } from "@ember/helper";
import { action } from "@ember/object";
import { trackedObject } from "@ember/reactive/collections";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { schedule } from "@ember/runloop";
import { service } from "@ember/service";
import discourseDebounce from "discourse/lib/debounce";
import { INPUT_DELAY, isTesting } from "discourse/lib/environment";
import { resettableTracked } from "discourse/lib/tracked-tools";
import DiscourseURL, {
  applyQueryParams,
  searchParamsFromPath,
} from "discourse/lib/url";
import { and, eq, not, or } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DFilterInput from "discourse/ui-kit/d-filter-input";
import DNativeSelect from "discourse/ui-kit/d-native-select";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";

const ResetButton = <template>
  <DButton
    class="btn-default d-filter-controls__reset"
    @action={{@action}}
    @ariaLabel="filter_controls.reset"
    @disabled={{@disabled}}
    @icon="arrow-rotate-left"
    @label={{@label}}
    @title="filter_controls.reset"
  />
</template>;

/**
 * filter controls component that support both client-side and server-side filtering
 *
 * client: provide searchableProps and filterFn in dropdownOptions
 * server: provide onTextFilterChange or onDropdownFilterChange callbacks
 *
 * The inlineFilters block places controls beside the search input, outside the drawer.
 *
 * @component DFilterControls
 * @param {Array} array - The dataset to display
 * @param {Array} [searchableProps] - Property names to search for client-side text filtering, can be dot-separated
 *                                for nested properties (e.g. "user.name")
 * @param {Array|Object} [dropdownOptions] - Dropdown options. Format: [{value, label, filterFn?}]. Or, if you
 *                                   want multiple dropdowns, format is: { dropdown1: [...], dropdown2: [...] }
 * @param {String} [inputPlaceholder] - Placeholder text for search input
 * @param {String|Object} [defaultDropdownValue="all"] - Default dropdown value(s). For single dropdown: "all",
 *                                                       for multiple: { dropdown1: "all", dropdown2: "all" }
 * @param {String|Object} [dropdownValue] - Current dropdown value(s), defaults to defaultDropdownValue
 * @param {Boolean} [showDropdownFilterToggle] - Whether to render the dropdown drawer toggle. Defaults to true for multiple dropdowns or when forced.
 * @param {Boolean} [forceShowDropdownFilterToggle=false] - Whether to place a single dropdown behind the filter toggle
 * @param {String} [noResultsMessage] - Message shown when no results found
 * @param {Boolean} [showNoResults=true] - Whether to show the built-in no-results state
 * @param {Boolean} [showTextFilter=true] - Whether to show the text filter input
 * @param {Boolean} [showResetButton=true] - Whether to show the reset filters button
 * @param {Boolean} [loading] - Whether data is loading (hides reset button during loading)
 * @param {Number} [minItemsForFilter] - Minimum items before showing filters (default: always show)
 * @param {Function} [onTextFilterChange] - Callback for text changes (enables server-side mode)
 * @param {Function} [onDropdownFilterChange] - Callback for dropdown changes (enables server-side mode).
 *                                              For multiple dropdowns: receives (key, value)
 * @param {Function} [onDropdownChange] - Callback for dropdown selection changes
 * @param {Function} [onResetFilters] - Callback for reset action (server-side mode)
 * @param {Function} [onFilterDropdownsToggle] - Callback fired when the dropdown drawer is opened or
 *                                               closed, receiving the new expanded state as a boolean
 * @param {String} [toggleLabel] - Optional translated label for the filter drawer button.
 * @param {Boolean} [additionalFiltersActive=false] - Whether filters rendered in the additionalFilters block are active.
 *                                                     The block renders alongside the dropdowns, sharing the drawer
 * @param {String} [initialTextFilter] - Initial value to seed the text filter input on mount
 * @param {Boolean} [showCustomEmptyState] - Whether to show a custom empty state when no results found,
 *                                           if minItemsForFilter is set and the array is empty
 */

export default class DFilterControls extends Component {
  @service router;

  @tracked dropdownFilter = "all";
  @tracked
  showFilterDropdowns = this.args.filterDropdownsExpanded ?? isTesting();
  @resettableTracked textFilter = this.initialTextFilterValue;

  dropdownFilters = trackedObject();

  constructor() {
    super(...arguments);

    this.applyDropdownValues();

    if (this.hasUrlOwnedDropdowns) {
      // keep URL-owned dropdowns in sync across real transitions (e.g. a
      // same-route link with different params); our own replaceState writes
      // don't fire this event, so user selections are never reverted
      this.router.on("routeDidChange", this.applyDropdownsFromUrl);
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    if (this.hasUrlOwnedDropdowns) {
      this.router.off("routeDidChange", this.applyDropdownsFromUrl);
    }
  }

  get initialTextFilterValue() {
    if (!this.showTextFilter) {
      return "";
    }

    // reading router.currentURL makes this getter re-evaluate on transitions,
    // so same-route links carrying the query param re-seed the input
    const fromUrl =
      this.args.textFilterQueryParam && this.showFilters
        ? this.currentSearchParams.get(this.args.textFilterQueryParam)
        : null;

    if (fromUrl !== null) {
      // verbatim: this value round-trips through our own URL writes while the
      // user is typing, so any normalization here would rewrite the input
      // mid-edit and jump the caret
      return fromUrl;
    }

    return this.args.initialTextFilter?.replace(/,\s*/g, ", ") || "";
  }

  get currentSearchParams() {
    // router.currentURL, unlike window.location, is also correct under the
    // test environment's mock location
    return searchParamsFromPath(this.router.currentURL);
  }

  get hasUrlOwnedDropdowns() {
    return (
      (this.args.dropdownFilterQueryParam ||
        this.args.dropdownFilterQueryParams) &&
      !("dropdownValue" in this.args)
    );
  }

  get array() {
    return Array.isArray(this.args.array) ? this.args.array : [];
  }

  get searchableProps() {
    return Array.isArray(this.args.searchableProps)
      ? this.args.searchableProps
      : [];
  }

  get hasMultipleDropdowns() {
    return (
      !Array.isArray(this.dropdownOptions) &&
      typeof this.dropdownOptions === "object"
    );
  }

  get dropdownOptions() {
    return this.args.dropdownOptions || [];
  }

  get showDropdownFilterToggle() {
    return (
      this.args.showDropdownFilterToggle ??
      (this.hasMultipleDropdowns || this.args.forceShowDropdownFilterToggle)
    );
  }

  get showDropdownFilter() {
    return (
      (this.dropdownOptions.length > 1 &&
        !this.args.forceShowDropdownFilterToggle) ||
      (this.hasMultipleDropdowns && this.showFilterDropdowns) ||
      (this.args.forceShowDropdownFilterToggle && this.showFilterDropdowns)
    );
  }

  // additional filters share the dropdown row, so they follow the drawer's
  // state; with no drawer to hide behind they render on their own
  get showStandaloneAdditionalFilters() {
    return !this.showDropdownFilterToggle;
  }

  get showTextFilter() {
    return this.args.showTextFilter ?? true;
  }

  get showResetButton() {
    return this.args.showResetButton ?? true;
  }

  get defaultDropdownValue() {
    return this.args.defaultDropdownValue || "all";
  }

  get dropdownValue() {
    return this.args.dropdownValue || this.defaultDropdownValue;
  }

  get showFilters() {
    return this.args.minItemsForFilter
      ? this.array.length >= this.args.minItemsForFilter
      : true;
  }

  get showNoResults() {
    return this.args.showNoResults ?? true;
  }

  get hasActiveFilters() {
    if (this.args.additionalFiltersActive) {
      return true;
    }

    if (this.textFilter.length > 0) {
      return true;
    }

    if (this.hasMultipleDropdowns) {
      return Object.keys(this.dropdownFilters).some((key) => {
        return this.dropdownFilters[key] !== this.defaultValue(key);
      });
    }

    return this.dropdownFilter !== this.defaultDropdownValue;
  }

  get filteredData() {
    let filtered = [...this.array];

    // skip if we have external callbacks (server-side)
    const hasExternalCallbacks =
      this.args.onTextFilterChange || this.args.onDropdownFilterChange;
    if (hasExternalCallbacks) {
      return filtered;
    }

    if (this.textFilter.length > 0) {
      const terms = this.textFilter
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      filtered = filtered.filter((item) =>
        terms.some((term) =>
          this.searchableProps.some((key) => {
            const value = this.getNestedValue(item, key);
            return value && value.toString().toLowerCase().includes(term);
          })
        )
      );
    }

    if (this.hasMultipleDropdowns) {
      Object.keys(this.dropdownFilters).forEach((key) => {
        const selectedValue = this.dropdownFilters[key];
        const options = this.dropdownOptions[key] || [];
        const selectedOption = options.find(
          (option) => option.value === selectedValue
        );
        if (selectedOption?.filterFn) {
          filtered = filtered.filter(selectedOption.filterFn);
        }
      });
    } else if (this.dropdownFilter !== this.defaultDropdownValue) {
      const selectedOption = this.dropdownOptions.find(
        (option) => option.value === this.dropdownFilter
      );
      if (selectedOption?.filterFn) {
        filtered = filtered.filter(selectedOption.filterFn);
      }
    }

    return filtered;
  }

  get singleDropdownLabel() {
    return this.dropdownOptions.find(
      (option) => option.value === this.defaultDropdownValue
    )?.label;
  }

  /**
   * Allows searchable props in the format user.name, this function gets the
   * nested value based on a dot-separated path.
   *
   * @param {object} obj - The object to get value from
   * @param {String} path - The property path (e.g. "user.name")
   * @returns {unknown} The value at the path
   */
  getNestedValue(obj, path) {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  }

  // @action so it stays bound to `this` when invoked from the template
  @action
  defaultValue(key) {
    const defaults =
      typeof this.defaultDropdownValue === "object"
        ? this.defaultDropdownValue
        : {};
    return defaults[key] || "all";
  }

  @action
  dropdownLabel(key) {
    const options = this.dropdownOptions[key] || [];
    return options.find((option) => option.value === this.defaultValue(key))
      ?.label;
  }

  @action
  applyDropdownsFromUrl() {
    if (!this.showFilters || !this.hasUrlOwnedDropdowns) {
      return;
    }

    const params = this.currentSearchParams;

    if (this.hasMultipleDropdowns) {
      for (const [key, paramName] of Object.entries(
        this.args.dropdownFilterQueryParams ?? {}
      )) {
        const defaultValue = this.defaultValue(key);
        const value =
          this.#validatedUrlValue(
            params,
            paramName,
            this.dropdownOptions[key] ?? []
          ) ?? defaultValue;

        this.dropdownFilters[key] = value;
        this.showFilterDropdowns ||= value !== defaultValue;
      }
    } else if (this.args.dropdownFilterQueryParam) {
      this.dropdownFilter =
        this.#validatedUrlValue(
          params,
          this.args.dropdownFilterQueryParam,
          this.dropdownOptions
        ) ?? this.defaultDropdownValue;
    }
  }

  dropdownValueFor(key) {
    const values =
      typeof this.dropdownValue === "object" ? this.dropdownValue : {};
    return values[key] || this.defaultValue(key);
  }

  @action
  applyDropdownValues() {
    if (this.hasMultipleDropdowns) {
      Object.keys(this.dropdownOptions).forEach((key) => {
        this.dropdownFilters[key] = this.dropdownValueFor(key);
      });
    } else {
      this.dropdownFilter = this.dropdownValue;
    }
  }

  syncTextFilterQueryParam() {
    this.updateQueryParams({
      [this.args.textFilterQueryParam]: this.textFilter,
    });
  }

  updateQueryParams(updates) {
    // no currentURL means the router isn't live (e.g. rendering tests)
    if (!this.showFilters || !this.router.currentURL) {
      return;
    }

    DiscourseURL.replaceState(
      applyQueryParams(this.router.currentURL, updates)
    );
  }

  @action
  onTextFilterChange(event) {
    this.textFilter = event.target?.value || "";

    if (this.args.textFilterQueryParam) {
      discourseDebounce(this, this.syncTextFilterQueryParam, INPUT_DELAY);
    }

    this.args.onTextFilterChange?.(event);
  }

  @action
  onDropdownFilterChange(keyOrValue, value) {
    const multiple = this.hasMultipleDropdowns;
    const selectedValue = multiple ? value : keyOrValue;
    const paramName = multiple
      ? this.args.dropdownFilterQueryParams?.[keyOrValue]
      : this.args.dropdownFilterQueryParam;
    const defaultValue = multiple
      ? this.defaultValue(keyOrValue)
      : this.defaultDropdownValue;

    if (multiple) {
      this.dropdownFilters[keyOrValue] = value;
    } else {
      this.dropdownFilter = keyOrValue;
    }

    if (paramName) {
      this.updateQueryParams({
        [paramName]: selectedValue === defaultValue ? null : selectedValue,
      });
    }

    if (multiple) {
      this.args.onDropdownFilterChange?.(keyOrValue, value);
      this.args.onDropdownChange?.(keyOrValue, value);
    } else {
      this.args.onDropdownFilterChange?.(keyOrValue);
      this.args.onDropdownChange?.(keyOrValue);
    }
  }

  @action
  async resetFilters() {
    this.textFilter = "";

    if (this.hasMultipleDropdowns) {
      Object.keys(this.dropdownFilters).forEach((key) => {
        const defaultValue = this.defaultValue(key);
        this.dropdownFilters[key] = defaultValue;
        this.args.onDropdownChange?.(key, defaultValue);
      });
    } else {
      this.dropdownFilter = this.defaultDropdownValue;
      this.args.onDropdownChange?.(this.defaultDropdownValue);
    }

    const queryParams = [
      this.args.textFilterQueryParam,
      this.args.dropdownFilterQueryParam,
      ...Object.values(this.args.dropdownFilterQueryParams ?? {}),
    ].filter(Boolean);
    if (queryParams.length) {
      this.updateQueryParams(
        Object.fromEntries(queryParams.map((name) => [name, null]))
      );
    }

    await this.args.onResetFilters?.();

    schedule("afterRender", () => {
      (
        document.querySelector(".d-filter-controls__input") ||
        document.querySelector(".d-filter-controls__toggle-filters") ||
        document.querySelector(".d-filter-controls__dropdown")
      )?.focus();
    });
  }

  @action
  toggleFilters() {
    this.showFilterDropdowns = !this.showFilterDropdowns;
    this.args.onFilterDropdownsToggle?.(this.showFilterDropdowns);
  }

  #validatedUrlValue(params, paramName, options) {
    const value = params.get(paramName);
    return value !== null && options.some((option) => option.value === value)
      ? value
      : null;
  }

  <template>
    {{yield to="aboveFilters"}}

    {{#if this.showFilters}}
      <div
        class={{dConcatClass
          "d-filter-controls"
          (if
            (or this.hasMultipleDropdowns @forceShowDropdownFilterToggle)
            "--dropdowns-in-filter-drawer"
          )
        }}
        {{didInsert this.applyDropdownsFromUrl}}
        {{didUpdate
          this.applyDropdownValues
          @defaultDropdownValue
          @dropdownValue
        }}
      >
        {{#if (or this.showTextFilter this.showDropdownFilterToggle)}}
          <div class="d-filter-controls__inputs">
            {{#if this.showTextFilter}}
              <DFilterInput
                class="d-filter-controls__input"
                placeholder={{@inputPlaceholder}}
                @filterAction={{this.onTextFilterChange}}
                @icons={{hash left="magnifying-glass"}}
                @value={{this.textFilter}}
              />
            {{/if}}

            {{yield to="inlineFilters"}}

            {{#if this.showDropdownFilterToggle}}
              <DButton
                aria-expanded={{if this.showFilterDropdowns "true" "false"}}
                class="btn-default d-filter-controls__toggle-filters"
                @action={{this.toggleFilters}}
                @icon={{if
                  this.showDropdownFilter
                  "filter-circle-xmark"
                  "filter"
                }}
                @title="filter_controls.toggle"
                @translatedLabel={{@toggleLabel}}
              />
              {{#if this.showResetButton}}
                <ResetButton
                  @action={{this.resetFilters}}
                  @disabled={{not this.hasActiveFilters}}
                />
              {{/if}}
            {{/if}}
          </div>
        {{/if}}

        {{#if this.showDropdownFilter}}
          <div class="d-filter-controls__dropdowns">
            {{#if this.hasMultipleDropdowns}}
              {{#each-in this.dropdownOptions as |key options|}}
                <DNativeSelect
                  aria-label={{this.dropdownLabel key}}
                  class={{dConcatClass
                    "d-filter-controls__dropdown"
                    (concat "d-filter-controls__dropdown--" key)
                    (unless
                      (eq
                        (get this.dropdownFilters key) (this.defaultValue key)
                      )
                      "--active"
                    )
                  }}
                  data-dropdown-key={{key}}
                  @includeNone={{false}}
                  @onChange={{fn this.onDropdownFilterChange key}}
                  @value={{get this.dropdownFilters key}}
                  as |select|
                >
                  {{#each options as |option|}}
                    <select.Option @value={{option.value}}>
                      {{option.label}}
                    </select.Option>
                  {{/each}}
                </DNativeSelect>
              {{/each-in}}
            {{else if this.dropdownOptions.length}}
              <DNativeSelect
                aria-label={{this.singleDropdownLabel}}
                class={{dConcatClass
                  "d-filter-controls__dropdown"
                  (unless
                    (eq this.dropdownFilter this.defaultDropdownValue)
                    "--active"
                  )
                }}
                @includeNone={{false}}
                @onChange={{this.onDropdownFilterChange}}
                @value={{this.dropdownFilter}}
                as |select|
              >
                {{#each this.dropdownOptions as |option|}}
                  <select.Option @value={{option.value}}>
                    {{option.label}}
                  </select.Option>
                {{/each}}
              </DNativeSelect>
            {{/if}}

            {{yield to="additionalFilters"}}
          </div>
        {{else if
          (and
            this.showStandaloneAdditionalFilters (has-block "additionalFilters")
          )
        }}
          <div class="d-filter-controls__additional-filters">
            {{yield to="additionalFilters"}}
          </div>
        {{/if}}

        {{#if (and this.showResetButton (not this.showDropdownFilterToggle))}}
          <ResetButton
            @action={{this.resetFilters}}
            @disabled={{not this.hasActiveFilters}}
          />
        {{/if}}

        {{yield to="actions"}}
      </div>
    {{/if}}

    {{yield to="aboveContent"}}

    {{#if this.filteredData.length}}
      {{yield this.filteredData to="content"}}
    {{else if this.showFilters}}
      {{#if (and this.showNoResults this.hasActiveFilters (not @loading))}}
        <div class="d-filter-controls__no-results">
          {{#if @noResultsMessage}}
            <p>{{@noResultsMessage}}</p>
          {{/if}}
          {{#if this.showResetButton}}
            <ResetButton
              @action={{this.resetFilters}}
              @label="filter_controls.reset"
            />
          {{/if}}
        </div>
      {{/if}}
    {{else}}
      {{#if @showCustomEmptyState}}
        {{yield to="customEmptyState"}}
      {{else}}
        {{yield this.array to="content"}}
      {{/if}}
    {{/if}}
  </template>
}
